import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
const MainLayout = lazy(() => import("@/components/layout/MainLayout").then(m => ({ default: m.MainLayout })));
import { useAuth } from "@/hooks/useAuth";
import { useSessionGuard } from "@/hooks/useSessionGuard";
import { useResponsiveTheme } from "@/hooks/useResponsiveTheme";
import { ScrollToTop } from "@/components/ScrollToTop";

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const AssistantPage = lazy(() => import("./pages/AssistantPage"));
const FilesPage = lazy(() => import("./pages/FilesPage"));
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));
const CasesPage = lazy(() => import("./pages/CasesPage"));
const CaseDetailPage = lazy(() => import("./pages/CaseDetailPage"));
const RecordsPage = lazy(() => import("./pages/RecordsPage"));
const ArchivePage = lazy(() => import("./pages/ArchivePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const TeamTasksPage = lazy(() => import("./pages/TeamTasksPage"));
const TeamPerformancePage = lazy(() => import("./pages/TeamPerformancePage"));
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));
const LegalMemoPage = lazy(() => import("./pages/LegalMemoPage"));
const DocumentAnalysisPage = lazy(() => import("./pages/DocumentAnalysisPage"));
const SmartSearchPage = lazy(() => import("./pages/SmartSearchPage"));
const SmartInterrogationPage = lazy(() => import("./pages/SmartInterrogationPage"));
const FinancialReportsPage = lazy(() => import("./pages/FinancialReportsPage"));
const CaseRollPage = lazy(() => import("./pages/CaseRollPage"));
const SubscribedOfficesPage = lazy(() => import("./pages/SubscribedOfficesPage"));
const BackupPage = lazy(() => import("./pages/BackupPage"));
const ConsultationLawyerHome = lazy(() => import("./pages/ConsultationLawyerHome"));
const CompanyHomePage = lazy(() => import("./pages/CompanyHomePage"));
const ClientIntakePage = lazy(() => import("./pages/ClientIntakePage"));
const IntakeFormPage = lazy(() => import("./pages/IntakeFormPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ClientHomePage = lazy(() => import("./pages/ClientHomePage"));
const StudentHomePage = lazy(() => import("./pages/StudentHomePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LawyerProfilePage = lazy(() => import("./pages/LawyerProfilePage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminAccountsPage = lazy(() => import("./pages/AdminAccountsPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));

const queryClient = new QueryClient();

const ProtectedRoute = ({ children, allowedType, teamLeadOnly }: { children: React.ReactNode; allowedType?: "lawyer" | "client" | "student"; teamLeadOnly?: boolean }) => {
  const { user, loading } = useAuth();
  useSessionGuard();
  const userType = localStorage.getItem("userType");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(!!teamLeadOnly);

  useEffect(() => {
    if (!teamLeadOnly) return;

    if (!user) {
      setRoleLoading(false);
      return;
    }

    // Always verify role server-side; never trust localStorage flags
    supabase.rpc("get_user_office_role", { _user_id: user.id }).then(({ data }) => {
      setUserRole(data);
      setRoleLoading(false);
    });
  }, [teamLeadOnly, user]);

  const hasTeamLeadAccess = userRole === "owner" || userRole === "admin";

  // For lawyer routes, require real Supabase session
  if (allowedType === "lawyer") {
    if (loading || roleLoading) {
      return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
    }
    if (!user) {
      // Clear stale localStorage
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("userType");
      localStorage.removeItem("isTeamLead");
      return <Navigate to="/auth" replace />;
    }
  } else {
    // Client/Student routes - also require real Supabase session
    if (loading) {
      return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
    }
    if (!user) {
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("userType");
      return <Navigate to="/auth" replace />;
    }
  }

  // If allowedType is specified, check if user has the right type
  if (allowedType && userType !== allowedType) {
    if (userType === "lawyer") return <Navigate to="/dashboard" replace />;
    if (userType === "student") return <Navigate to="/student-home" replace />;
    return <Navigate to="/client-home" replace />;
  }

  // If teamLeadOnly, check server-side role (owner or admin)
  if (teamLeadOnly && !hasTeamLeadAccess) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};


const ResponsiveThemeApplier = ({ children }: { children: React.ReactNode }) => {
  useResponsiveTheme();
  return <>{children}</>;
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <ResponsiveThemeApplier>
    <LanguageProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<AuthPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/sitemap.xml" element={<SitemapPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/intake/:lawyerId" element={<IntakeFormPage />} />
            <Route path="/lawyer-profile/:id" element={<LawyerProfilePage />} />
            <Route path="/admin-panel" element={<AdminAccountsPage />} />
            <Route path="/fares-ahmed" element={<AdminAccountsPage />} />
            
            {/* Client Routes */}
            <Route
              path="/client-home"
              element={<ClientHomePage />}
            />
            
            {/* Student Routes */}
            <Route
              path="/student-home"
              element={<StudentHomePage />}
            />
            
            {/* Consultation Lawyer Routes */}
            <Route
              path="/consultation-lawyer-home"
              element={<ConsultationLawyerHome />}
            />

            {/* Company Routes */}
            <Route
              path="/company-home"
              element={<CompanyHomePage />}
            />
            
            {/* Lawyer Routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute allowedType="lawyer">
                  <MainLayout>
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/calendar" element={<CalendarPage />} />
                      <Route path="/clients" element={<ClientsPage />} />
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/team-tasks" element={<ProtectedRoute teamLeadOnly><TeamTasksPage /></ProtectedRoute>} />
                      <Route path="/team-performance" element={<ProtectedRoute teamLeadOnly><TeamPerformancePage /></ProtectedRoute>} />
                      <Route path="/sessions" element={<SessionsPage />} />
                      <Route path="/assistant" element={<AssistantPage />} />
                      <Route path="/files" element={<FilesPage />} />
                      <Route path="/announcements" element={<AnnouncementsPage />} />
                      <Route path="/cases" element={<CasesPage />} />
                      <Route path="/cases/:id" element={<CaseDetailPage />} />
                      <Route path="/records" element={<RecordsPage />} />
                      <Route path="/case-roll" element={<CaseRollPage />} />
                      <Route path="/archive" element={<ArchivePage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/finances" element={<FinancialReportsPage />} />
                      
                      <Route path="/client-intake" element={<ClientIntakePage />} />
                      <Route path="/report" element={<ReportPage />} />
                      <Route path="/legal-memo" element={<LegalMemoPage />} />
                      <Route path="/analysis" element={<DocumentAnalysisPage />} />
                      <Route path="/smart-search" element={<SmartSearchPage />} />
                      <Route path="/smart-interrogation" element={<SmartInterrogationPage />} />
                      <Route path="/backup" element={<BackupPage />} />
                      <Route path="/subscribed-offices" element={<ProtectedRoute teamLeadOnly><SubscribedOfficesPage /></ProtectedRoute>} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </LanguageProvider>
    </ResponsiveThemeApplier>
  </ThemeProvider>
);

export default App;
